import React, { Fragment, useId, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
    Ban,
    CalendarClock,
    ChevronDown,
    CircleCheck,
    CirclePercent,
    Clock,
    CreditCard,
    ExternalLink,
    Info,
    Link,
    Mail,
    Percent,
    ShieldCheck,
    User,
} from 'lucide-react';

import { toPolicyView } from './policyView';
import './cancellationPolicy.css';

const ICON = { strokeWidth: 1.75, 'aria-hidden': true };

const Stat = ({ icon, label, children, className = '' }) => (
    <div className={`cxp-stat ${className}`}>
        <span className="cxp-stat-icon">{icon}</span>
        <div className="cxp-stat-body">
            <div className="cxp-label">{label}</div>
            {children}
        </div>
    </div>
);

const ParentCard = ({ parent }) => {
    const { available, method, tiers, maxPct } = parent;

    if (!available) {
        return (
            <section className="cxp-card cxp-card-no">
                <div className="cxp-head">
                    <span className="cxp-avatar cxp-avatar-no"><User {...ICON} /></span>
                    <div className="cxp-head-text">
                        <h3 className="cxp-head-title">No Refund Available</h3>
                        <p className="cxp-head-sub">If you need to cancel your registration, a refund is not available.</p>
                    </div>
                    <div className="cxp-figure cxp-figure-no">
                        <span className="cxp-figure-num">0%</span>
                        <span className="cxp-figure-word">Refund</span>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="cxp-card cxp-card-yes">
            <div className="cxp-head">
                <span className="cxp-avatar cxp-avatar-yes"><CircleCheck {...ICON} /></span>
                <div className="cxp-head-text">
                    <h3 className="cxp-head-title">Refund Available</h3>
                    <p className="cxp-head-sub">If you need to cancel your registration, refunds are provided according to the terms below.</p>
                </div>
                {tiers.length > 0 ? (
                    <div className="cxp-figure cxp-figure-yes">
                        <span className="cxp-figure-pre">Up to</span>
                        <span className="cxp-figure-num">{maxPct}%</span>
                        <span className="cxp-figure-word">Refund</span>
                    </div>
                ) : (
                    // No usable tiers: the method is the only detail, so it sits in the header row
                    // instead of taking a whole panel of height.
                    <Stat className="cxp-head-stat" icon={<CreditCard {...ICON} />} label="Refund Method">
                        <div className="cxp-value">{method}</div>
                    </Stat>
                )}
            </div>

            {tiers.length > 0 && (
                <div className="cxp-panel cxp-panel-yes cxp-cols-2">
                    <Stat icon={<CreditCard {...ICON} />} label="Refund Method">
                        <div className="cxp-value">{method}</div>
                    </Stat>
                    <Stat icon={<CalendarClock {...ICON} />} label="Refund Schedule">
                        <ul className="cxp-tiers">
                            {tiers.map((tier, i) => (
                                <li key={i}>
                                    <span className="cxp-tier-pct">{tier.pct}%</span>
                                    <span>refund · cancel at least <b>{tier.days}</b> {tier.days === 1 ? 'day' : 'days'} before start</span>
                                </li>
                            ))}
                        </ul>
                    </Stat>
                </div>
            )}
        </section>
    );
};

const INSURANCE_CLAIM_URL = 'https://vicoverage.com/customer-portal/';
const INSURANCE_EMAIL = 'support@vicoverage.com';

// Only rendered when the camp gives no parent refund. `insurancePurchased` is undefined at
// checkout (nothing bought yet, so the card is information only) and a boolean on a booking.
const InsuranceCard = ({ insurancePurchased }) => (
    <section className="cxp-card cxp-card-insurance">
        <div className="cxp-ins">
            <span className="cxp-ins-icon"><ShieldCheck {...ICON} /></span>
            <div className="cxp-ins-body">
                <h3 className="cxp-ins-title">Cancellation Insurance</h3>
                <p className="cxp-ins-text">
                    All tuition and registration fees are non-refundable unless cancellation insurance was purchased at
                    registration and the cancellation qualifies for coverage under the policy.
                </p>

                {insurancePurchased === true && (
                    <Fragment>
                        <p className="cxp-ins-note">
                            You purchased cancellation insurance with this booking, so you may be eligible for reimbursement
                            through the insurance provider, subject to the terms and conditions of your coverage.{' '}
                            <b>Canceling your registration does not automatically initiate an insurance claim.</b>
                        </p>
                        <div className="cxp-ins-actions">
                            <a className="cxp-ins-claim" href={INSURANCE_CLAIM_URL} target="_blank" rel="noopener noreferrer">
                                How to file a claim <ExternalLink {...ICON} />
                            </a>
                            <span className="cxp-ins-contact">
                                <Mail {...ICON} />
                                For Questions &amp; Claim Status:{' '}
                                <a href={`mailto:${INSURANCE_EMAIL}`}>{INSURANCE_EMAIL}</a>
                            </span>
                        </div>
                    </Fragment>
                )}

                {insurancePurchased === false && (
                    <p className="cxp-ins-note">
                        No cancellation insurance was purchased with this booking. <b>Tuition is non-refundable.</b>
                    </p>
                )}
            </div>
        </div>
    </section>
);

// A card whose body folds away under its header. Closed by default so the popup stays short;
// the body stays mounted and the CSS grid-row transition does the animating.
const PolicyAccordion = ({ icon, title, summary, className = '', children }) => {
    const [open, setOpen] = useState(false);
    const panelId = useId();

    return (
        <section className={`cxp-card cxp-acc ${open ? 'cxp-acc-open' : ''} ${className}`}>
            <button
                type="button"
                className="cxp-acc-head"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen(v => !v)}>
                <span className="cxp-acc-icon">{icon}</span>
                <span className="cxp-acc-text">
                    <span className="cxp-acc-title">{title}</span>
                    <span className="cxp-acc-sub">{summary}</span>
                </span>
                <span className="cxp-acc-toggle">{open ? 'Hide' : 'Details'}</span>
                <ChevronDown {...ICON} className="cxp-acc-chevron" />
            </button>
            <div id={panelId} role="region" className="cxp-acc-panel" aria-hidden={!open}>
                <div className="cxp-acc-inner">
                    <div className="cxp-acc-content">{children}</div>
                </div>
            </div>
        </section>
    );
};

const ProviderCard = ({ campName, provider }) => {
    const { available, method, pct } = provider;

    return (
        <PolicyAccordion
            className="cxp-card-provider"
            icon={<Percent {...ICON} />}
            title={<Fragment>If <span className="cxp-red">{campName}</span> Cancels</Fragment>}
            summary={available
                ? `If ${campName} cancels the program, you will receive a refund on the terms below.`
                : `If ${campName} cancels the program, a refund is not available.`}>
            <div className="cxp-panel cxp-cols-3">
                <Stat icon={<CreditCard {...ICON} />} label="Refund Method">
                    <div className="cxp-value">{available ? method : 'Not available'}</div>
                </Stat>
                <Stat icon={<CirclePercent {...ICON} />} label="Refund Amount">
                    <div className="cxp-value cxp-value-lg">{available ? (pct === null ? '—' : `${pct}%`) : '0%'}</div>
                </Stat>
                <Stat icon={<Info {...ICON} />} label="Applies When">
                    <div className="cxp-text">The program is cancelled due to low enrollment or other extenuating circumstances.</div>
                </Stat>
            </div>
        </PolicyAccordion>
    );
};

const InfoCard = () => (
    <PolicyAccordion
        className="cxp-card-info"
        icon={<Info {...ICON} />}
        title="Refund & Cancellation Information"
        summary="Registration fees, the 24-hour policy, and multi-program terms.">
        <div className="cxp-info-cols cxp-cols-3">
            <div className="cxp-info-col">
                <span className="cxp-info-icon"><Ban {...ICON} /></span>
                <div>
                    <h4>Non-Refundable Registration Fee</h4>
                    <p>If a Non-Refundable Registration Fee is listed, then that fee is non-refundable regardless of any refund terms listed otherwise.</p>
                </div>
            </div>
            <div className="cxp-info-col">
                <span className="cxp-info-icon"><Clock {...ICON} /></span>
                <div>
                    <h4>24-Hour Refund Policy</h4>
                    <p>The 24-hour refund policy applies to all fees and purchases, at all times.</p>
                </div>
            </div>
            <div className="cxp-info-col">
                <span className="cxp-info-icon cxp-info-icon-amber">
                    <Link {...ICON} strokeWidth={2.25} />
                </span>
                <div>
                    <h4>Different Programs, Different Terms</h4>
                    <p>Cancellation terms vary by program. If you're purchasing multiple programs from <b>different providers</b>, click the yellow button next to each program to check their terms.</p>
                </div>
            </div>
        </div>
    </PolicyAccordion>
);

// The policy cards on their own, for pages that embed them in a modal of their own
// (e.g. the Cancel Booking modal in ViewBooking). `item` is anything carrying the camp's saved
// refund/cancellation fields plus `campName` — a cart item or a booking detail.
// `insurancePurchased` is only passed for an existing booking; see InsuranceCard.
export const CancellationPolicyCards = ({ item, insurancePurchased, className = '' }) => {
    const view = toPolicyView(item);

    return (
        <div className={`cxp ${className}`}>
            <ParentCard parent={view.parent} />
            {!view.parent.available && <InsuranceCard insurancePurchased={insurancePurchased} />}
            <ProviderCard campName={view.campName} provider={view.provider} />
            <InfoCard />
        </div>
    );
};

const CancellationPolicyModal = ({ item, onClose }) => (
    <Modal
        dialogClassName="h_auto cxp-dialog"
        show={!!item}
        onHide={onClose}
        animation={false}
        centered>
        <Modal.Body>
            {item && (
                <Fragment>
                    <span className="modal_close" onClick={onClose}><i className="icon-cross" /></span>
                    <CancellationPolicyCards item={item} className="cxp-in-modal" />
                </Fragment>
            )}
        </Modal.Body>
    </Modal>
);

export default CancellationPolicyModal;
